import React, { Suspense, lazy } from 'react';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import theme from './theme';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import { isAdminUser, isEmployeeUser, isSuperAdminUser, getPostLoginPath } from './utils/authRoles';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

const RegistrationPage = lazy(() => import('./pages/RegistrationPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const EmployeeDashboardPage = lazy(() => import('./pages/EmployeeDashboardPage'));
const EmployeeRegistrationPage = lazy(() => import('./pages/EmployeeRegistrationPage'));
const ManageSubadminPage = lazy(() => import('./pages/ManageSubadminPage'));
const GuestOrientationPage = lazy(() => import('./pages/GuestOrientationPage'));

const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      bgcolor: 'background.default',
    }}
  >
    <CircularProgress size={36} />
  </Box>
);

const LazyPage = ({ children }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);

const PublicRoute = ({ children }) => {
  const { user } = useAuth();

  if (user) {
    return <Navigate to={getPostLoginPath(user)} replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!isAdminUser(user)) {
    return <Navigate to="/my-registration" replace />;
  }

  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!isSuperAdminUser(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const EmployeeRoute = ({ children }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!isEmployeeUser(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const GuestOrAdminRoute = ({ children }) => {
  const { user } = useAuth();

  if (user && !isAdminUser(user)) {
    return <Navigate to="/my-registration" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route
              path="/login"
              element={(
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              )}
            />
            <Route
              path="/registration"
              element={(
                <PublicRoute>
                  <LazyPage>
                    <RegistrationPage />
                  </LazyPage>
                </PublicRoute>
              )}
            />
            <Route
              path="/forgot-password"
              element={(
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              )}
            />
            <Route
              path="/reset-password"
              element={(
                <PublicRoute>
                  <ResetPasswordPage />
                </PublicRoute>
              )}
            />
            <Route
              path="/dashboard"
              element={(
                <AdminRoute>
                  <LazyPage>
                    <DashboardPage />
                  </LazyPage>
                </AdminRoute>
              )}
            />
            <Route
              path="/manage-subadmin"
              element={(
                <SuperAdminRoute>
                  <LazyPage>
                    <ManageSubadminPage />
                  </LazyPage>
                </SuperAdminRoute>
              )}
            />
            <Route
              path="/my-registration"
              element={(
                <EmployeeRoute>
                  <LazyPage>
                    <EmployeeDashboardPage />
                  </LazyPage>
                </EmployeeRoute>
              )}
            />
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route
              path="/employee-registration"
              element={(
                <GuestOrAdminRoute>
                  <LazyPage>
                    <EmployeeRegistrationPage />
                  </LazyPage>
                </GuestOrAdminRoute>
              )}
            />
            <Route
              path="/employee-documents"
              element={(
                <EmployeeRoute>
                  <LazyPage>
                    <EmployeeRegistrationPage />
                  </LazyPage>
                </EmployeeRoute>
              )}
            />
            <Route
              path="/guest-orientation"
              element={(
                <LazyPage>
                  <GuestOrientationPage />
                </LazyPage>
              )}
            />
            <Route
              path="/employee-orientation"
              element={(
                <EmployeeRoute>
                  <LazyPage>
                    <EmployeeRegistrationPage />
                  </LazyPage>
                </EmployeeRoute>
              )}
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
